import { getSafeReturnUrl } from '../../utils/userAddingRoleToken';
import { tokenText } from './roleTokens/tokenText';
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * USER PROFILE PAGE — VERSION 5.2.1 DYNAMIC (fixed)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CASCADE STRICTE à 3 niveaux:
 *
 *   NIVEAU 1 — pageSettings.sections[]  (DB AnimationSettings)
 *   NIVEAU 2 — pageSettings.facets[]    (DB AnimationSettings)
 *   NIVEAU 3 — PAGES_CONFIG_.USER_PROFILE (statique)
 *
 * CORE_SECTIONS (toujours présentes et enabled):
 *   userInfoCard | userButtons | userModal
 *   → AnimationSettings peut modifier order/props, PAS les supprimer.
 *
 * FIXES v5.2.1:
 *   - handePage: un seul setState atomique au lieu de 3
 *   - Query params ?addr/?tel nettoyés après ouverture modal
 *   - Sync modalState avec guard sur changement réel
 *   - loadPageData dans les deps des useEffect (ou stabilisé)
 *   - DEFAULT_FACETS inclut CORE_SECTIONS explicitement
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Layout from '../Layout/Layout';
import { isAuthenticated } from '../auth';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer from '../pageInside/exeemple_page/DynamicPageRenderer';
import { getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import {
    PAGES_CONFIG_,
    SECTIONS_LIBRARY,
} from '../pageInside/exeemple_page/pagesConfig';
import { elakayAutoText } from "../pageInside/dataElakay";
import AgentProfileManager from "./AgentProfileManager";


// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES MODULE
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_ID = 'USER_PROFILE';

/**
 * CORE_SECTIONS — toujours présentes, toujours enabled.
 * AnimationSettings peut modifier order/props mais pas les supprimer.
 */
const CORE_SECTIONS = ['userInfoCard', 'userButtons', 'userModal'];

// Validation: chaque CORE doit exister dans SECTIONS_LIBRARY
(() => {
    CORE_SECTIONS.forEach(id => {
        if (!SECTIONS_LIBRARY[id]) {
            console.warn(
                `[UserProfile] CORE_SECTION "${id}" absent de SECTIONS_LIBRARY. ` +
                `Elle sera ignorée au rendu.`
            );
        }
    });
})();

// ═══════════════════════════════════════════════════════════════════════════
// NIVEAU 3 — defaults depuis PAGES_CONFIG_.USER_PROFILE
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];

    // Fallback si config absente : CORE + userView
    if (!config?.sections?.length) {
        return [...CORE_SECTIONS, 'userView'];
    }

    const fromConfig = config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);

    // Garantir que les CORE sont toujours dans DEFAULT_FACETS
    // (cas edge: config.sections n'inclut pas tous les CORE)
    CORE_SECTIONS.forEach(coreId => {
        if (!fromConfig.includes(coreId)) fromConfig.unshift(coreId);
    });

    return fromConfig;
})();

const DEFAULT_CONFIG_OVERRIDES = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) return {};

    const result = {};
    config.sections.forEach(s => {
        if (!s.sectionId) return;
        const entry = {};
        const lib = SECTIONS_LIBRARY[s.sectionId];

        if (lib?.defaultProps || s.props) {
            entry.props = { ...(lib?.defaultProps || {}), ...(s.props || {}) };
        }
        if (s.overrides && Object.keys(s.overrides).length > 0) {
            entry.overrides = { ...s.overrides };
        }
        if (Object.keys(entry).length > 0) {
            result[s.sectionId] = entry;
        }
    });
    return result;
})();

// ═══════════════════════════════════════════════════════════════════════════
// HELPER — useQuery
// ═══════════════════════════════════════════════════════════════════════════

const useQuery = () => new URLSearchParams(useLocation().search);

// ═══════════════════════════════════════════════════════════════════════════
// _extractFromSettings — NIVEAUX 1 & 2
// ═══════════════════════════════════════════════════════════════════════════

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    // ── Priorité 1 : sections[] ─────────────────────────────────────────────
    if (Array.isArray(sections) && sections.length > 0) {
        const mergedMap = new Map();

        sections.forEach(s => {
            if (!s.sectionId) return;
            mergedMap.set(s.sectionId, {
                sectionId: s.sectionId,
                order:     s.order ?? 0,
                // CORE → toujours enabled ; OPTIONAL/EXTRA → respecter le flag
                enabled:   CORE_SECTIONS.includes(s.sectionId)
                               ? true
                               : s.enabled !== false,
                props:     s.props     || {},
                overrides: s.overrides || {},
            });
        });

        // Garantir la présence des CORE absents (order négatif → tête de liste)
        CORE_SECTIONS.forEach((coreId, idx) => {
            if (!mergedMap.has(coreId)) {
                mergedMap.set(coreId, {
                    sectionId: coreId,
                    order:     -(CORE_SECTIONS.length - idx), // -3, -2, -1
                    enabled:   true,
                    props:     {},
                    overrides: {},
                });
            }
        });

        const sorted = [...mergedMap.values()]
            .sort((a, b) => a.order - b.order);

        const resolvedFacets = sorted
            .filter(s => s.enabled)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (!s.sectionId) return;
            const entry = {};
            if (s.props     && Object.keys(s.props).length > 0)     entry.props     = s.props;
            if (s.overrides && Object.keys(s.overrides).length > 0) entry.overrides = s.overrides;
            if (Object.keys(entry).length > 0) configOverrides[s.sectionId] = entry;
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    // ── Priorité 2 : facets[] ───────────────────────────────────────────────
    if (Array.isArray(facets) && facets.length > 0) {
        // Garantir les CORE même en mode facets[]
        const base = [...facets];
        CORE_SECTIONS.forEach(coreId => {
            if (!base.includes(coreId)) base.unshift(coreId);
        });
        return { facets: base, configOverrides: {} };
    }

    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// resolvePageConfig — CASCADE STRICTE 3 niveaux
// ═══════════════════════════════════════════════════════════════════════════

const resolvePageConfig = (pageSettings) => {
    const result = _extractFromSettings(pageSettings);
    if (result) return result;

    return {
        facets:          DEFAULT_FACETS,
        configOverrides: DEFAULT_CONFIG_OVERRIDES,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// MODAL STATE HELPER — atomique
// ═══════════════════════════════════════════════════════════════════════════

const INITIAL_MODAL_STATE = {
    showModal:   false,
    pageAddress: false,
    pagePhone:   false,
};

const nextModalState = (page) => {
    switch (page) {
        case 'pageAddress': return { showModal: true,  pageAddress: true,  pagePhone: false };
        case 'pagePhone':   return { showModal: true,  pageAddress: false, pagePhone: true  };
        default:            return INITIAL_MODAL_STATE;
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const UserProfile = (props) => {
    const {
        infoSource,
        laguangeElakay,
        homeStyles,
        nbrArr = 3,
    } = props;

    const location  = useLocation();
    const tokenReturn = getSafeReturnUrl(new URLSearchParams(location.search).get('returnUrl'));
    const workflowReturn = tokenReturn && !tokenReturn.startsWith('/profile') ? tokenReturn : null;
    const showTokenReturn = tokenReturn?.startsWith('/user-step?') && new URLSearchParams(tokenReturn.split('?')[1]).has('t');
    const navigate  = useNavigate();
    const { token } = isAuthenticated();

    // ────────────────────────────────────────────────────────────────────────
    // 1. MODAL STATE — un seul state object au lieu de 3 useState
    // ────────────────────────────────────────────────────────────────────────

    const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);

    const handePage = useCallback((page) => {
        if (!page && workflowReturn) {
            navigate(workflowReturn, { replace: true });
            return;
        }
        setModalState(nextModalState(page));
    }, [navigate, workflowReturn]);

    // ── Ouvrir modal depuis query params, puis nettoyer l'URL ────────────────
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const addr   = params.get('addr');
        const tel    = params.get('tel');

        if (!addr && !tel) return;

        if (addr) handePage('pageAddress');
        else if (tel) handePage('pagePhone');

        // Nettoyer l'URL pour éviter réouverture au remount
        params.delete('addr');
        params.delete('tel');
        const cleanSearch = params.toString();
        navigate(
            `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`,
            { replace: true }
        );
    }, [location.search, location.pathname, handePage, navigate]);

    // ────────────────────────────────────────────────────────────────────────
    // 2. CASCADE — résoudre config depuis AnimationSettings
    // ────────────────────────────────────────────────────────────────────────

    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];

    const { facets, configOverrides: baseConfigOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        // Deps: stringify stabilise le compare sur contenu structurel
        [
            JSON.stringify(pageSettings?.sections),
            JSON.stringify(pageSettings?.facets),
        ]
    );

    // ────────────────────────────────────────────────────────────────────────
    // 3. CONFIG OVERRIDES finaux
    //    Injecte handePage dans userButtons et userModal sans écraser
    //    les props AnimationSettings existantes.
    // ────────────────────────────────────────────────────────────────────────

    const configOverrides = useMemo(() => ({
        ...baseConfigOverrides,
        userButtons: {
            ...(baseConfigOverrides.userButtons || {}),
            props: {
                ...(baseConfigOverrides.userButtons?.props || {}),
                handePage,
            },
        },
        userModal: {
            ...(baseConfigOverrides.userModal || {}),
            props: {
                ...(baseConfigOverrides.userModal?.props || {}),
                handePage,
            },
        },
    }), [baseConfigOverrides, handePage]);

    // ────────────────────────────────────────────────────────────────────────
    // 4. DATA LOADER
    // ────────────────────────────────────────────────────────────────────────

    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
    });

    // ────────────────────────────────────────────────────────────────────────
    // 5. SYNC infoSource → pageData (sections loaderType NONE)
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) {
            mergePageData(updates);
        }
    }, [infoSource, facets, mergePageData]);

    // ────────────────────────────────────────────────────────────────────────
    // 6. SYNC modalState → pageData.modalState
    //    Guard: ne merge que si le state a réellement changé
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        mergePageData({ modalState });
    }, [modalState, mergePageData]);

    // ────────────────────────────────────────────────────────────────────────
    // 7. CHARGEMENT — reload si facets changent
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(facets)]);

    // ────────────────────────────────────────────────────────────────────────
    // 8. STYLES
    // ────────────────────────────────────────────────────────────────────────

    const myStyles = useMemo(() => ({
        width:           '100%',
        backgroundColor: '#f0f0f0',
        ...homeStyles,
    }), [homeStyles]);

    // ────────────────────────────────────────────────────────────────────────
    // 9. RENDER
    // ────────────────────────────────────────────────────────────────────────

    return (
        <Layout
            infoSource={infoSource}
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).userProfile.t001}
            description={elakayAutoText("h_41682905073222", "Node React E-commerce App")}
            className="container-fluid"
            SliderPunShow={false}
            loading={loading}
            {...infoSource?.allSettings?.layout}
        >
            <div style={infoSource.styleNav}>
                <div className="p-2">
                    {showTokenReturn && <Link className="btn btn-outline-primary mb-3" to={tokenReturn}>{tokenText(infoSource)('eventToken')} — {tokenText(infoSource)('continue')}</Link>}
                    <DynamicPageRenderer
                        facets={facets}
                        pageId={PAGE_ID}
                        pageData={pageData}
                        infoSource={infoSource}
                        laguangeElakay={laguangeElakay}
                        homeStyles={myStyles}
                        nbrArr={nbrArr}
                        reloadFacets={reloadFacets}
                        configOverrides={configOverrides}
                    />
                    <AgentProfileManager
                        infoSource={infoSource}
                        onSaved={() => reloadFacets?.()}
                        onManagePhone={() => handePage("pagePhone")}
                    />
                </div>
            </div>
        </Layout>
    );
};

export default UserProfile;
